/**
 * Key exchange service — shares EC P-384 public keys via the @keys relay user.
 *
 * Each user has two public keys:
 *   - encrypt_key: ECDH P-384 public key (for ECDH key agreement)
 *   - sign_key:    ECDSA P-384 public key (for signature verification)
 *
 * Both are stored as JWK strings in the backend's public_keys table and in
 * local IndexedDB via keyStorageService.
 */

import {
  generateKeyPair,
  base64ToArrayBuffer,
  arrayBufferToBase64,
  importEncryptPublicKey,
  importSignPublicKey,
} from "@/utils/crypto";
import {
  storeKeyPair,
  storePublicKey,
  getActiveKeySet,
  getActiveKeyId,
  hasPrivateKey,
} from "./keyStorageService";
import { sendUserMessage } from "./conversationService";
import { KEYS_USERNAME } from "@/constants/message";

export { KEYS_USERNAME };

const EXTERNAL_ADDRESS_TYPE = 4;

export const E2EE_CMD_GET_ALL = "getallkeys";
export const E2EE_CMD_GET = "getkeys";
export const E2EE_CMD_SEND = "sendkeys";
export const E2EE_CMD_DELETE = "deletekeys";

export interface KeyExchangeMessage {
  cls?: "keys";
  cmd?: string;
  user_id?: number;
  username?: string;
  encrypt_key?: string;
  sign_key?: string;
  timestamp_generated?: number;
  public_keys?: KeyExchangeMessage[];
  error?: string;
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

// Dedup concurrent initializeEncryption calls for the same user.
// On first login, multiple hook instances + the websocket middleware all call
// this simultaneously. Without the guard each generates a different key pair,
// races to write it, and ends up with a stale keyId. With it, they all await
// the same promise and receive the same keyId.
const initInFlight = new Map<number, Promise<string>>();

// Resolvers waiting for a specific username's key to arrive via WebSocket.
// requestPublicKey registers here before sending the HTTP request so the
// immediate retry in encryptionProcessor can wait for the actual arrival.
const pendingKeyArrivals = new Map<string, Array<() => void>>();

/**
 * Notifies any waiting requestPublicKey callers that a key for `username`
 * has been stored in IndexedDB. Called from handleReceivedPublicKey.
 */
export function notifyKeyArrived(username: string): void {
  const resolvers = pendingKeyArrivals.get(username);
  if (resolvers?.length) {
    pendingKeyArrivals.delete(username);
    resolvers.forEach((r) => r());
  }
}

// Resolved key cache: survives logout/re-login within the same JS runtime.
// Avoids the async IDB round-trip on every re-login so useEncryption can
// skip the isLoading→isInitialized transition entirely.
const resolvedKeyIds = new Map<number, string>();

/** Returns the cached keyId for userId if already resolved this session, else null. */
export function getResolvedKeyId(userId: number): string | null {
  return resolvedKeyIds.get(userId) ?? null;
}

/**
 * Generates an EC P-384 key pair set if one doesn't exist, stores it locally,
 * and returns the key ID. Concurrent calls for the same userId are coalesced.
 * Subsequent calls return immediately from the in-memory cache.
 */
export async function initializeEncryption(userId: number): Promise<string> {
  // Fast path: already resolved in this JS runtime (re-login without page reload)
  const cached = resolvedKeyIds.get(userId);
  if (cached) return cached;

  const existing = initInFlight.get(userId);
  if (existing) return existing;

  const promise = (async () => {
    const hasKey = await hasPrivateKey(userId);

    if (hasKey) {
      const keyId = await getActiveKeyId(userId);
      if (keyId) {
        console.log("Encryption already initialized with key:", keyId);
        resolvedKeyIds.set(userId, keyId);
        return keyId;
      }
    }

    console.log("Generating new EC P-384 key pairs...");
    const keyPairInfo = await generateKeyPair();
    await storeKeyPair(userId, keyPairInfo, true);
    console.log("Key pairs generated and stored. Key ID:", keyPairInfo.keyId);
    resolvedKeyIds.set(userId, keyPairInfo.keyId);

    return keyPairInfo.keyId;
  })();

  initInFlight.set(userId, promise);
  promise.finally(() => initInFlight.delete(userId));
  return promise;
}

// ---------------------------------------------------------------------------
// Key sharing
// ---------------------------------------------------------------------------

/**
 * Builds the `sendkeys <payload>` command string to upload both public keys
 * to the @keys relay.
 *
 * Keys are sent as X509/SPKI Base64 so both Android (BouncyCastle X509EncodedKeySpec)
 * and web (crypto.subtle.importKey("spki", ...)) can parse them.
 * The receive path (`parsePublicKeyString`) handles both SPKI and legacy JWK formats
 * for backward compatibility.
 */
export async function createPublicKeyShareMessage(userId: number): Promise<string> {
  const keySet = await getActiveKeySet(userId);
  if (!keySet) throw new Error("No active key set. Initialize encryption first.");

  const [encryptCryptoKey, signCryptoKey] = await Promise.all([
    importEncryptPublicKey(keySet.encryptPublicKeyJwk),
    importSignPublicKey(keySet.signPublicKeyJwk),
  ]);
  const [encryptSpki, signSpki] = await Promise.all([
    crypto.subtle.exportKey("spki", encryptCryptoKey),
    crypto.subtle.exportKey("spki", signCryptoKey),
  ]);

  const payload = {
    encrypt_key: arrayBufferToBase64(encryptSpki),
    sign_key: arrayBufferToBase64(signSpki),
    // Use the key's own creation time, not the current time, so the timestamp
    // stays stable across browser sessions (matches Android's keyGenTimeStamp behaviour).
    timestamp_generated: Math.floor(keySet.createdAt / 1000),
  };

  return `${E2EE_CMD_SEND} ${JSON.stringify(payload)}`;
}

// ---------------------------------------------------------------------------
// Key requests
// ---------------------------------------------------------------------------

export async function createPublicKeyRequestMessage(targetUsername: string): Promise<string> {
  return `${E2EE_CMD_GET} ${targetUsername}`;
}

export function parseKeyExchangeMessage(message: string): KeyExchangeMessage | null {
  try {
    const parsed = JSON.parse(message);
    if (
      parsed.cls === "keys" &&
      (parsed.cmd === E2EE_CMD_GET || parsed.cmd === E2EE_CMD_GET_ALL)
    ) {
      return parsed as KeyExchangeMessage;
    }
    return null;
  } catch {
    return null;
  }
}

export async function sendKeySystemCommand(command: string, userId: number): Promise<any> {
  return await sendUserMessage({
    cmd: "SendUserMessage",
    message: command,
    address: KEYS_USERNAME,
    addressId: 0,
    address_type: EXTERNAL_ADDRESS_TYPE,
    lat: 0,
    lng: 0,
    media: null,
    media_filename: null,
    media_type: 0,
    sendtime: Date.now(),
    userid: userId,
  });
}

// How long to wait for the @keys WebSocket reply before giving up.
const KEY_ARRIVAL_TIMEOUT_MS = 5000;

/**
 * Requests a user's public key from @keys and waits for it to arrive.
 *
 * The backend processes `getkeys <username>` synchronously during the HTTP
 * request but delivers the result as a received WebSocket message (via
 * saveRMessage → WebSocket → processAndDispatch → handleReceivedPublicKey).
 * Registering a resolver in pendingKeyArrivals BEFORE sending ensures the
 * arrival notification is never missed even if the WS reply races ahead.
 */
export async function requestPublicKey(
  targetUsername: string,
  currentUserId: number
): Promise<void> {
  // Register the arrival listener before sending so we never miss a fast reply.
  const keyArrival = new Promise<void>((resolve) => {
    const timeoutId = setTimeout(() => {
      const resolvers = pendingKeyArrivals.get(targetUsername) ?? [];
      const idx = resolvers.indexOf(wrappedResolve);
      if (idx !== -1) resolvers.splice(idx, 1);
      resolve();
    }, KEY_ARRIVAL_TIMEOUT_MS);

    const wrappedResolve = () => {
      clearTimeout(timeoutId);
      resolve();
    };

    const existing = pendingKeyArrivals.get(targetUsername) ?? [];
    existing.push(wrappedResolve);
    pendingKeyArrivals.set(targetUsername, existing);
  });

  const command = await createPublicKeyRequestMessage(targetUsername);
  await sendKeySystemCommand(command, currentUserId);
  await keyArrival;
}

// Last time we successfully fetched a key for each username, keyed by username.
// Shared across all callers in the same JS runtime (tab), reset on page load.
const lastKeyFetchAt = new Map<string, number>();

// How long a fetched key is considered fresh. Within this window, repeated
// encrypt() calls skip the network round-trip. After it expires, or when the
// peer's key is missing entirely, a fresh fetch is always performed.
const KEY_FRESHNESS_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches the latest public key for `targetUsername` only if the locally
 * cached copy is older than KEY_FRESHNESS_MS. Called before every outbound
 * encryption to catch peer re-logins (Android generates a new key pair on
 * every login) without making a network call on every message send.
 *
 * Non-fatal: falls through to whatever is cached in IndexedDB on failure.
 */
export async function ensureFreshPublicKey(
  targetUsername: string,
  currentUserId: number
): Promise<void> {
  const lastFetch = lastKeyFetchAt.get(targetUsername) ?? 0;
  if (Date.now() - lastFetch < KEY_FRESHNESS_MS) return;
  try {
    // Set optimistically before awaiting to prevent concurrent fetches for the
    // same peer (e.g. rapid sends) from all launching simultaneous requests.
    lastKeyFetchAt.set(targetUsername, Date.now());
    await requestPublicKey(targetUsername, currentUserId);
  } catch (err) {
    lastKeyFetchAt.delete(targetUsername); // allow retry next time
    console.warn("[E2EE] ensureFreshPublicKey failed for", targetUsername, err);
  }
}


export async function requestAllPublicKeys(currentUserId: number): Promise<void> {
  const command = E2EE_CMD_GET_ALL;
  const response = await sendKeySystemCommand(command, currentUserId);

  if (response?.message) {
    const parsed = parseKeyExchangeMessage(response.message);
    if (parsed && parsed.cmd === E2EE_CMD_GET_ALL && parsed.public_keys) {
      for (const keyInfo of parsed.public_keys) {
        await handleReceivedPublicKey(keyInfo);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Receiving a peer's public key
// ---------------------------------------------------------------------------

/**
 * Parses a public key string that may be either:
 *   - JWK JSON  (web clients): {"kty":"EC","crv":"P-384","x":"...","y":"..."}
 *   - X509/SPKI Base64 (Android clients): DER-encoded public key, base64-encoded
 *
 * Returns the key as a JWK so the rest of the codebase stays uniform.
 */
export async function parsePublicKeyString(
  keyStr: string,
  algorithm: "ECDH" | "ECDSA"
): Promise<JsonWebKey | null> {
  // Try JWK first (web clients)
  try {
    const parsed = JSON.parse(keyStr);
    if (parsed && typeof parsed === "object" && parsed.kty === "EC") {
      return parsed as JsonWebKey;
    }
  } catch { /* not JSON — try X509/SPKI */ }

  // Try X509/SPKI Base64 (Android clients)
  try {
    const spkiBytes = base64ToArrayBuffer(keyStr);
    const params =
      algorithm === "ECDH"
        ? { name: "ECDH", namedCurve: "P-384" }
        : { name: "ECDSA", namedCurve: "P-384" };
    const usages: KeyUsage[] = algorithm === "ECDH" ? [] : ["verify"];
    const cryptoKey = await crypto.subtle.importKey("spki", spkiBytes, params, true, usages);
    return crypto.subtle.exportKey("jwk", cryptoKey);
  } catch (err) {
    console.warn("[E2EE] Failed to parse key as X509/SPKI:", err);
    return null;
  }
}

/**
 * Stores both public keys received from another user via the @keys relay.
 * Accepts both JWK (web) and X509/SPKI Base64 (Android) key formats.
 * `sign_key` defaults to `encrypt_key` if absent (backward compat with older
 * clients that only sent one key).
 *
 * @returns true if a usable key was stored, false if the message was skipped.
 */
export async function handleReceivedPublicKey(message: KeyExchangeMessage): Promise<boolean> {
  if (!message.encrypt_key || !message.user_id || !message.username) {
    throw new Error("Invalid public key message from backend");
  }

  const encryptPublicKeyJwk = await parsePublicKeyString(message.encrypt_key, "ECDH");

  if (!encryptPublicKeyJwk) {
    console.warn(`[E2EE] Could not parse encrypt key for ${message.username} — skipping`);
    return false;
  }

  // Reject legacy RSA keys — only EC P-384 keys are compatible with the current
  // ECDH encryption scheme. Users with old RSA keys need to re-login to regenerate.
  if (encryptPublicKeyJwk.kty !== "EC" || encryptPublicKeyJwk.crv !== "P-384") {
    console.warn(`[E2EE] Ignoring non-EC key for ${message.username} (kty=${encryptPublicKeyJwk.kty})`);
    return false;
  }

  const signPublicKeyJwk = message.sign_key
    ? (await parsePublicKeyString(message.sign_key, "ECDSA")) ?? encryptPublicKeyJwk
    : encryptPublicKeyJwk;

  await storePublicKey(
    "imported-" + message.user_id,
    message.user_id,
    message.username,
    encryptPublicKeyJwk,
    signPublicKeyJwk,
    message.timestamp_generated
  );

  console.log(`Stored public keys for user ${message.username} (ID: ${message.user_id})`);
  notifyKeyArrived(message.username);
  return true;
}

export function isKeyExchangeMessage(message: string): boolean {
  return parseKeyExchangeMessage(message) !== null;
}
